import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  MDBDatatable,
  MDBFooter,
  MDBBadge,
  MDBRow,
  MDBCol,
  MDBDropdown,
  MDBDropdownToggle,
  MDBDropdownMenu,
  MDBDropdownItem,
  MDBIcon,
  MDBCheckbox,
} from "mdb-react-ui-kit";
import { useCookies } from "react-cookie";
import Header from "./Header";
import Pagination from "./Pagination";
import {
  filterValidValues,
  getFirstNonFiscalResult,
  processContentWhenReady,
  getSponsorPayload,
  convertISOToReadableTimeFormat,
  convertToISOString,
  createFullUrl,
} from "./BillTableUtils";

const BillsTableCurrent = ({
                             sessionId,
                             legislatureId,
                             subjectCode,
                             setSideNavToggle,
                             sideNavToggle,
                             selectedBillTypes,
                             selectedLegislator,
                             offset,
                             setOffset,
                             selectedDrafter,
                             selectedParties,
                             selectedChambers,
                             selectedCommittee,
                             selectedProgress,
                             hasAmendment,
                             hasFiscalNotes,
                             hasLegalNotes,
                             selectedDeadlines,
                             selectedActionTypes,
                             statusStartDate,
                             statusEndDate,
                             selectedChapters,
                             sessionInfoForHeader,
                             hasPreIntroductionRequired,
                             requestor,
                             requestorType,
                             byRequestOf,
                             byRequestOfType,
                           }) => {
  const [cookies, setCookie] = useCookies(["selectedColumns"]);

  const [allRequestorData, setAllRequestorData] = useState([]);
  const [requestorData, setRequestorData] = useState(null);
  const [fetchRequestorError, setFetchRequestorError] = useState(null);
  const [noResultsMessage, setNoResultsMessage] = useState("Loading Bills...");
  const [bills, setBills] = useState({ columns: [], rows: [] });
  const [totalPages, setTotalPages] = useState(0);
  const [lastPage, setLastPage] = useState(false);
  const [totalElements, setTotalElements] = useState(false);
  const [loading, setLoading] = useState(true);
  const [billSearchText, setBillSearchText] = useState("");
  const [exportDataReady, setExportDataReady] = useState(false);
  const [selectedHeaderValue, setSelectedHeaderValue] = useState([
    "LC #",
    "Bill #",
    "Short Title",
    "Primary Sponsor",
    "Status Date",
    "Status",
  ]);
  const [billTypes, setBillTypes] = useState([]);
  const [abortControllerQueue, setAbortControllerQueue] = useState([]);

  const [selectHeaders, setSelectHeaders] = useState([
    { text: "Bill #", value: "Bill #" },
    { text: "LC #", value: "LC #" },
    { text: "Requester", value: "Requestor" },
    { text: "Primary Sponsor", value: "Primary Sponsor" },
    { text: "By Request Of", value: "By Request Of" },
    { text: "Chapter", value: "Chapter" },
  ]);
  const [tableHeading, setTableHeading] = useState([
    {
      label: "Bill #",
      field: "Bill #",
      sort: false,
      width: "auto",
      displayHeader: true,
      dynamicHeader: true,
    },
    {
      label: "LC #",
      field: "LC #",
      sort: false,
      width: "auto",
      displayHeader: true,
      dynamicHeader: true,
    },
    {
      label: "Short Title",
      field: "Short Title",
      sort: false,
      width: 400,
      displayHeader: true,
      dynamicHeader: false,
    },
    {
      label: "Requester",
      field: "Requestor",
      sort: false,
      width: "auto",
      displayHeader: false,
      dynamicHeader: true,
    },
    {
      label: "By Request Of",
      field: "By Request Of",
      sort: false,
      width: "auto",
      displayHeader: false,
      dynamicHeader: true,
    },
    {
      label: "Primary Sponsor",
      field: "Primary Sponsor",
      sort: false,
      width: "auto",
      displayHeader: true,
      dynamicHeader: true,
    },
    {
      label: "Chapter",
      field: "Chapter",
      sort: false,
      width: "auto",
      displayHeader: false,
      dynamicHeader: true,
    },
    {
      label: "Status Date",
      field: "Status Date",
      sort: false,
      width: "auto",
      displayHeader: true,
      dynamicHeader: false,
    },
    {
      label: "Status",
      field: "Status",
      sort: false,
      width: "auto",
      displayHeader: true,
      dynamicHeader: false,
    },
  ]);
  const [dataForExport, setDataForExport] = useState(tableHeading);

  // Add new state for export
  const [exportProgress, setExportProgress] = useState({
    loading: false,
    progress: 0,
    total: 0,
  });

  useEffect(() => {
    const fetchAllRequestors = async () => {
      try {
        if (!sessionId) {
          console.warn("Session ID is not available yet.");
          return;
        }

        const requestorApiCalls = [
          axios.post(
              `${process.env.REACT_APP_LAWS_BACKEND_HOST}/legislators/v1/legislators/search?limit=9999&offset=0`,
              { sessionIds: [sessionId] }
          ),
          axios.get(
              `${process.env.REACT_APP_LAWS_BACKEND_HOST}/committees/v1/standingCommittees/findBySessionId?sessionId=${sessionId}`
          ),
          axios.get(
              `${process.env.REACT_APP_LAWS_BACKEND_HOST}/committees/v1/nonStandingCommittees/findByLegislatureId?legislatureId=${sessionId}`
          ),
          axios.get(
              `${process.env.REACT_APP_LAWS_BACKEND_HOST}/legislators/v1/electedOfficialPositions`
          ),
          axios.get(
              `${process.env.REACT_APP_LAWS_BACKEND_HOST}/legislators/v1/organizations`
          ),
        ];
        const results = await Promise.all(requestorApiCalls);
        const extractedData = results.map((response) => response.data);
        setAllRequestorData(extractedData);
      } catch (error) {
        if (axios.isAxiosError(error)) {
          console.error(
              "Axios error fetching requestor data:",
              error.response ? error.response.data : error.message
          );
        } else {
          console.error("Error fetching requestor data:", error);
        }
        setFetchRequestorError(error);
      }
    };
    fetchAllRequestors();
  }, [sessionId]);

  useEffect(() => {
    const savedSelection = cookies.selectedColumns;
    if (savedSelection) {
      try {
        let parsedSelection;
        if (Array.isArray(savedSelection)) {
          parsedSelection = savedSelection;
        } else {
          parsedSelection = JSON.parse(savedSelection);
        }

        // Only use setSelectedHeaders as it handles both states
        setSelectedHeaders(parsedSelection);
      } catch (error) {
        console.error("Error parsing saved selection:", error);
        // If parsing fails, try to split the string
        if (typeof savedSelection === "string") {
          const fallbackSelection = savedSelection
              .split(",")
              .map((item) => item.trim());
          setSelectedHeaders(fallbackSelection);
        } else {
          // If all else fails, set to default
          setSelectedHeaders([
            "LC #",
            "Bill #",
            "Short Title",
            "Primary Sponsor",
            "Status Date",
            "Status",
          ]);
        }
      }
    } else {
      // No saved selection, use default
      setSelectedHeaders([
        "LC #",
        "Bill #",
        "Short Title",
        "Primary Sponsor",
        "Status Date",
        "Status",
      ]);
    }
  }, [cookies.selectedColumns]);

  const formatLawsBillStructureForDataTables = (content) => {
    return content.map((item) => {
      const sessionId = item.sessionId;

      const latestBillStatusSorted =
          item.draft.billStatuses.length > 0
              ? item.draft.billStatuses.sort(
                  (a, b) => new Date(b.timeStamp) - new Date(a.timeStamp)
              )
              : null;

      const latestBillStatus = getFirstNonFiscalResult(latestBillStatusSorted);

      // Simplified requestor display logic
      const requestorDisplay = () => {
        if (!item.requester)
          return (
              <MDBBadge color="secondary" light>
                Not Available
              </MDBBadge>
          );
        return <span>{item.requester}</span>;
      };

      const byRequestOfDisplay = () => {
        if (!item.byRequestOf)
          return (
              <MDBBadge color="secondary" light>
                Not Available
              </MDBBadge>
          );
        return <span>{item.byRequestOf}</span>;
      };

      return {
        "Bill #":
            item.billNumber && item.billType ? (
                <Link
                    to={`/laws/bill/${sessionId}/${item.draft.draftNumber}?open_tab=sum`}
                    className="text-decoration-underline legmt-link"
                    target="_blank"
                >
                  {item.billType.code} {item.billNumber}
                </Link>
            ) : (
                <MDBBadge color="secondary" light>
                  Not Available
                </MDBBadge>
            ),
        "LC #": item.draft.draftNumber ? (
            <>
              <Link
                  to={`/lc/bill/${sessionId}/${item.draft.draftNumber}`}
                  className="text-decoration-underline legmt-link"
                  target="_blank"
              >
                {item.draft.draftNumber}
              </Link>
              {item.lcTextAvailable ? (
                  <Link
                      to={`/lc/bill/${sessionId}/${item.draft.draftNumber}?open_tab=lctext`}
                      className="text-decoration-underline legmt-link mx-1"
                      target="_blank"
                  >
                    <MDBIcon far icon="file-pdf" />
                  </Link>
              ) : (
                  <></>
              )}
            </>
        ) : (
            <MDBBadge color="secondary" light>
              Not Available
            </MDBBadge>
        ),
        "Short Title": item.draft.shortTitle ? (
            <span>{item.draft.shortTitle}</span>
        ) : (
            <MDBBadge color="secondary" light>
              Not Available
            </MDBBadge>
        ),
        "Primary Sponsor": item.sponsor ? (
            <span>{`${item.sponsor.firstName} ${item.sponsor.lastName}`}</span>
        ) : (
            <MDBBadge color="secondary" light>
              Not Available
            </MDBBadge>
        ),
        "Status Date": latestBillStatus ? (
            <span>
            {convertISOToReadableTimeFormat(latestBillStatus.timeStamp)}
          </span>
        ) : (
            <MDBBadge color="secondary" light>
              Not Available
            </MDBBadge>
        ),
        Status: latestBillStatus ? (
            (() => {
              const actionCode = parseInt(latestBillStatus.billStatusCode.code);
              const isDangerCode =
                  actionCode === 40 ||
                  actionCode === 274 ||
                  actionCode === 275 ||
                  actionCode === 674 ||
                  actionCode === 675;

              if (isDangerCode) {
                return (
                    <MDBBadge color="danger" className="badge w-100" light>
                      {latestBillStatus.billStatusCode.name}
                    </MDBBadge>
                );
              } else {
                return (
                    <MDBBadge color="custom1" className="badge w-100">
                      {latestBillStatus.billStatusCode.name}
                    </MDBBadge>
                );
              }
            })()
        ) : (
            <MDBBadge color="secondary" light>
              Not Available
            </MDBBadge>
        ),
        Requestor: requestorDisplay(),
        "By Request Of": byRequestOfDisplay(),
        Chapter: item.sessionLawChapterNumber,
      };
    });
  };

  const formatLawsBillStructureForDataExport = (content, formatToExport) => {
    if (!content || !Array.isArray(content)) {
      console.error(
          "Invalid content provided to formatLawsBillStructureForDataExport"
      );
      return [];
    }

    console.log(`Formatting ${content.length} records for export`);

    const exportHeaders = tableHeading
        .filter((heading) => heading.displayHeader)
        .map((heading) => heading.label);

    console.log("Export headers:", exportHeaders);

    const formattedRecords = content.map((item, index) => {
      if (index % 1000 === 0) {
        console.log(`Formatting record ${index + 1} of ${content.length}`);
      }

      const latestBillStatus =
          item.draft.billStatuses.length > 0
              ? item.draft.billStatuses[item.draft.billStatuses.length - 1]
              : null;

      const billNumber =
          item.billNumber && item.billType
              ? `${item.billType.code} ${item.billNumber}`
              : "Not Available";

      const billUrl =
          item.billNumber && item.billType
              ? createFullUrl(`/#/laws/bill/${sessionId}/${item.draft.draftNumber}`)
              : "Not Available";

      const lcNumber = item.draft.draftNumber
          ? `${item.draft.draftNumber}`
          : "Not Available";

      const lcUrl = item.draft.draftNumber
          ? createFullUrl(`/#/lc/bill/${sessionId}/${item.draft.draftNumber}`)
          : "Not Available";

      const fullDataObject = {
        "Bill #": billNumber,
        "Bill URL": billUrl,
        "LC #": lcNumber,
        "LC URL": lcUrl,
        "Short Title": item.draft.shortTitle || "Not Available",
        Requester: item.requester || "Not Available",
        "By Request Of": item.byRequestOf || "Not Available",
        "Primary Sponsor": item.sponsor
            ? `${item.sponsor.firstName} ${item.sponsor.lastName}`
            : "Not Available",
        Chapter: item.sessionLawChapterNumber || "Not Available",
        "Status Date": latestBillStatus
            ? convertISOToReadableTimeFormat(latestBillStatus.timeStamp)
            : "Not Available",
        Status: latestBillStatus
            ? latestBillStatus.billStatusCode.name
            : "Not Available",
      };

      // Filter based on exportHeaders, but add Bill URL and LC URL manually
      const filteredDataObject = Object.keys(fullDataObject)
          .filter(
              (key) =>
                  exportHeaders.includes(key) ||
                  key === "Bill URL" ||
                  key === "LC URL"
          )
          .reduce((obj, key) => {
            obj[key] = fullDataObject[key];
            return obj;
          }, {});

      return filteredDataObject;
    });

    console.log(`Formatted ${formattedRecords.length} records for export`);
    return formattedRecords;
  };

  const getAllSponsors = async () => {
    const { data } = await axios.get(
        `${process.env.REACT_APP_LAWS_BACKEND_HOST}/legislators/v1/legislators`
    );
    return data;
  };

  const fetchBillTypes = async () => {
    try {
      const response = await axios.get(
          `${process.env.REACT_APP_LAWS_BACKEND_HOST}/bills/v1/billTypes`
      );
      setBillTypes(response.data);
    } catch (error) {
      console.error("Error fetching bill types:", error);
    }
  };

  useEffect(() => {
    fetchBillTypes();
  }, []);

  const getBillTypeId = (code) => {
    const billType = billTypes.find((type) => type.code === code.toUpperCase());
    return billType ? billType.id : null;
  };

  const parseNameFormat = (text) => {
    // Check for "Lastname, Firstname" format
    const nameRegex = /^([^,]+),\s*(.+)$/;
    const match = text.match(nameRegex);

    if (match) {
      const [, lastName, firstName] = match;
      return {
        lastName: lastName.trim(),
        firstName: firstName.trim(),
        isNameFormat: true,
      };
    }

    return {
      isNameFormat: false,
      searchText: text,
    };
  };

  const getSearchPayload = (text) => {
    const searchRequestorsPayloadPrep =
        allRequestorData && allRequestorData[0] && allRequestorData[0].content
            ? allRequestorData[0].content.map((legislator) => ({
              id: legislator.id,
              name: `${legislator.firstName} ${legislator.lastName}`,
              lastName: legislator.lastName,
              firstName: legislator.firstName,
            }))
            : [];

    const searchOrgsPayloadPrep =
        allRequestorData && allRequestorData[4] && allRequestorData[4]
            ? allRequestorData[4].map((orgs) => ({
              id: orgs.id,
              name: `${orgs.name}`,
            }))
            : [];

    const billRegex = /^(HB|HR|HJ|SB|SR|SJ)\s*(\d+)$/i;
    const lcRegex = /^LC\s*(\d+)$/i;
    const chapterRegex = /^chapter\s*(\d+)$/i;
    const dateRegex =
        /^(0?[1-9]|1[0-2])[/-](0?[1-9]|[12][0-9]|3[01])[/-](19|20)\d{2}$/;
    const billMatch = text.match(billRegex);
    const lcMatch = text.match(lcRegex);
    const chapterMatch = text.match(chapterRegex);
    const dateMatch = text.match(dateRegex);

    let payload = {};

    if (billMatch) {
      const [, billType, billNumber] = billMatch;
      payload = {
        billNumber: Number(billNumber),
        billType: billType.toUpperCase(),
      };
    } else if (lcMatch) {
      const [, lcNumber] = lcMatch;
      payload = {
        draftNumber: `${lcNumber}`,
      };
    } else if (chapterMatch) {
      const [, chapterNumber] = chapterMatch;
      payload = {
        sessionLawChapterNumber: Number(chapterNumber),
      };
    } else if (dateMatch) {
      // Convert the date to ISO format for the API
      const [, month, day, year] = dateMatch;
      const date = new Date(year, month - 1, day);
      const today = new Date();
      const startDate = date.toISOString().split("T")[0];
      const endDate = today.toISOString().split("T")[0];
      payload = {
        latestBillStatusEffectivePeriod: {
          startDate: startDate,
          endDate: endDate,
        },
      };
    } else if (!isNaN(text)) {
      payload = {
        billNumber: Number(text),
      };
    } else {
      const parsedName = parseNameFormat(text);

      if (parsedName.isNameFormat) {
        // Handle "Lastname, Firstname" format
        const matchedLegislators = searchRequestorsPayloadPrep.filter(
            (legislator) =>
                legislator.lastName.toLowerCase() ===
                parsedName.lastName.toLowerCase() &&
                legislator.firstName
                    .toLowerCase()
                    .includes(parsedName.firstName.toLowerCase())
        );

        if (matchedLegislators.length > 0) {
          payload.requesterLegislatorIds = matchedLegislators.map(
              (legislator) => legislator.id
          );
          payload.sponsorIds = matchedLegislators.map(
              (legislator) => legislator.id
          );
        }
      } else {
        // Handle regular text search
        payload = {
          draftNumber: text,
          shortTitle: text,
        };

        const matchedLegislators = searchRequestorsPayloadPrep.filter(
            (legislator) =>
                legislator.name.toLowerCase().includes(text.toLowerCase())
        );

        if (matchedLegislators.length > 0) {
          payload.requesterLegislatorIds = matchedLegislators.map(
              (legislator) => legislator.id
          );
        }
      }
    }

    const matchedOrgs = searchOrgsPayloadPrep.filter((orgs) =>
        orgs.name.toLowerCase().includes(text.toLowerCase())
    );

    if (matchedOrgs.length > 0) {
      payload.requesterOrganizationIds = matchedOrgs.map((orgs) => orgs.id);
    }

    return payload;
  };

  const getAllSessions = async (apiType) => {
    try {
      if (apiType === "LAWS") {
        const { data } = await axios.get(
            `${process.env.REACT_APP_LAWS_BACKEND_HOST}/legislators/v1/sessions`
        );
        return data;
      } else {
        // return response from ARCHIVE
        const { data } = await axios.get(
            `${process.env.REACT_APP_LAWS_BACKEND_HOST}/archive/v1/sessions`
        );
        return data;
      }
    } catch (error) {
      console.log({ error });
    }
  };

  const searchLawsAndSetBills = useCallback(
      async (resultLimit = 100, signal) => {
        const requestId = Date.now();
        const abortController = new AbortController();

        setAbortControllerQueue((prevQueue) => {
          Object.values(prevQueue).forEach((controller) => controller.abort());
          return { [requestId]: abortController };
        });

        setBills({ columns: tableHeading, rows: [] });
        setLoading(true);

        try {
          const [{ data }] = await Promise.all([
            fetchBillsData(signal || abortController.signal, resultLimit),
          ]);

          const { content, totalPages, last, totalElements } = data;
          const sponsors = await getAllSponsors();

          // Process content with whatever data we have
          let updatedContent = await processContentWhenReady(
              content,
              allRequestorData
          );

          const showPdfLink = content
              .filter((bill) => {
                const hasCode18 = bill.draft.billStatuses.some(
                    (status) => status.billStatusCode.code === "18"
                );
                const hasCode100or500 = bill.draft.billStatuses.some(
                    (status) =>
                        status.billStatusCode.code === "100" ||
                        status.billStatusCode.code === "500"
                );
                return hasCode18 && !hasCode100or500;
              })
              .map((bill) => bill.draft.draftNumber);

          updatedContent = content.map((bill) => {
            const sponsor = sponsors.find((s) => s.id === bill.sponsorId);
            const showLcPdf = showPdfLink.includes(bill.draft.draftNumber);
            bill["lcTextAvailable"] = showLcPdf;
            return { ...bill, sponsor: sponsor || null };
          });

          const formattedData = formatBillsData(updatedContent, tableHeading);

          if (formattedData.rows.length === 0) {
            setNoResultsMessage(`No results found`);
          } else {
            setNoResultsMessage(`Loading Bills...`);
          }
          setBills(formattedData);
          setTotalPages(totalPages);
          setTotalElements(totalElements);
          setLastPage(last);
        } catch (error) {
          if (error.name !== "AbortError") {
            handleError(error);
          }
        } finally {
          setLoading(false);
          setAbortControllerQueue((prevQueue) => {
            const newQueue = { ...prevQueue };
            delete newQueue[requestId];
            return newQueue;
          });
        }
      },
      [
        offset,
        legislatureId,
        selectedBillTypes,
        subjectCode,
        statusStartDate,
        statusEndDate,
        selectedChambers,
        selectedChapters,
        selectedCommittee,
        hasAmendment,
        hasFiscalNotes,
        hasLegalNotes,
        selectedProgress,
        selectedLegislator,
        selectedDeadlines,
        selectedDrafter,
        hasPreIntroductionRequired,
        billSearchText,
        requestor,
        requestorType,
        byRequestOf,
        byRequestOfType,
        selectedActionTypes,
        allRequestorData,
        tableHeading,
      ]
  );

  const buildSearchPayload = () => {
    const sponsorPayload = getSponsorPayload(
        billSearchText,
        selectedLegislator
    );
    const billTypeRegex = /^(HB|HR|HJ|SB|SR|SJ)\s*\d+$/i;
    const billTypeMatch = billSearchText.match(billTypeRegex);

    const optionalFieldsPayload = billSearchText
        ? getSearchPayload(billSearchText)
        : {};

    if (selectedLegislator && requestor && requestorType === "LEGISLATOR") {
      optionalFieldsPayload.requesterLegislatorIds = [requestor];
      optionalFieldsPayload.sponsorIds = [selectedLegislator];
      sponsorPayload.sponsorIds = [];
    }

    const payload = {
      sessionIds: [sessionId],
      billTypeIds: billTypeMatch
          ? [getBillTypeId(billTypeMatch[1])]
          : selectedBillTypes.map((billType) => billType.typeCodeId),
      subjectIds: Array.isArray(subjectCode) ? subjectCode : [subjectCode],
      chambers: selectedChambers.map((chamber) =>
          chamber === "S" ? "SENATE" : chamber === "H" ? "HOUSE" : ""
      ),
      hasAmendments: hasAmendment,
      fiscalNote: hasFiscalNotes,
      legalNote: hasLegalNotes,
      ...(selectedCommittee && { standingCommitteeIds: [selectedCommittee] }),
      parties: Array.isArray(selectedParties)
          ? selectedParties
          : [selectedParties],
      sponsorIds: sponsorPayload.sponsorIds || [],
      latestBillStatusCodeProgressCategoryIds: selectedProgress
          ? Array.isArray(selectedProgress)
              ? selectedProgress
              : [selectedProgress]
          : [],
      deadlineCodeIds: selectedDeadlines,
      drafterStaffMemberIds: selectedDrafter
          ? Array.isArray(selectedDrafter)
              ? selectedDrafter
              : [selectedDrafter]
          : [],
      optionalFields:
          Object.keys(optionalFieldsPayload).length > 0
              ? optionalFieldsPayload
              : false,
      preIntroRequired: hasPreIntroductionRequired,
      requesterFields:
          requestor && requestorType
              ? { id: requestor, type: requestorType }
              : false,
      byRequestOfFields:
          byRequestOf && byRequestOfType
              ? { id: byRequestOf, requestType: byRequestOfType }
              : false,
      billStatusCodeIds: selectedActionTypes,
      sessionLawChapterNumbers: selectedChapters || [],
      latestBillStatusEffectivePeriod:
          (statusStartDate || statusEndDate) && selectedActionTypes
              ? {
                ...(statusStartDate && {
                  startDate: convertToISOString(statusStartDate),
                }),
                ...(statusEndDate && {
                  endDate: convertToISOString(statusEndDate),
                }),
                billStatusCodeId: selectedActionTypes[0],
              }
              : [],
    };

    return filterValidValues(payload);
  };

  // Remove all previous search effects and replace with a single consolidated effect
  useEffect(() => {
    const controller = new AbortController();
    let timeoutId;

    const performSearch = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(() => {
        searchLawsAndSetBills(100, controller.signal).catch((error) => {
          if (error.name !== "AbortError") {
            console.error(error);
          }
        });
      }, 500);
    };

    // Perform initial search
    performSearch();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      controller.abort();
    };
  }, [
    // Critical dependencies
    offset,
    billSearchText,
    selectedBillTypes,
    selectedLegislator,
    selectedCommittee,
    selectedChambers,
    subjectCode,
    selectedParties,
    selectedChapters,
    // Secondary dependencies
    hasAmendment,
    hasFiscalNotes,
    hasLegalNotes,
    selectedProgress,
    selectedDeadlines,
    selectedDrafter,
    hasPreIntroductionRequired,
    requestor,
    requestorType,
    byRequestOf,
    byRequestOfType,
    selectedActionTypes,
    statusStartDate,
    statusEndDate,
    tableHeading,
  ]);

  const fetchBillsData = async (signal, resultLimit) => {
    const url = `${process.env.REACT_APP_LAWS_BACKEND_HOST}/bills/v1/bills/search?includeCounts=false&sort=billType.sortOrder,desc&sort=billNumber,asc&sort=draft.draftNumber,asc&limit=${resultLimit}&offset=${offset}`;
    return await axios.post(url, memoizedSearchPayload, { signal });
  };

  const abortAllRequests = () => {
    setAbortControllerQueue((prevQueue) => {
      Object.values(prevQueue).forEach((controller) => controller.abort());
      return {};
    });
  };
  useEffect(() => {
    return () => {
      abortAllRequests();
    };
  }, []);

  const formatBillsData = (updatedContent, tableHeading) => {
    const sortedContent = sortContent(updatedContent, tableHeading);
    const tableRowData = formatLawsBillStructureForDataTables(sortedContent);
    const filteredHeaders = tableHeading.filter(
        (header) => header.displayHeader !== false
    );
    return { columns: filteredHeaders, rows: tableRowData };
  };

  const sortContent = (content, tableHeading) => {
    if (!content || !Array.isArray(content) || content.length === 0) {
      return [];
    }
    return content;
  };

  const handleError = (error) => {
    if (axios.isCancel(error)) {
      //console.log('Request canceled');
    } else {
      console.error("Error fetching data: ", error);
      throw error;
    }
  };

  const loadExportData = async ({ formatToExport }) => {
    try {
      // Reset states
      setExportProgress({ loading: true, progress: 0, total: 0 });
      setExportDataReady(false);
      setDataForExport(null);

      // Get total count
      const countResponse = await axios.post(
          `${process.env.REACT_APP_LAWS_BACKEND_HOST}/bills/v1/bills/search?includeCounts=false&limit=1&offset=0`,
          memoizedSearchPayload
      );

      const totalItems = countResponse.data.totalElements;
      const pageSize = 100;
      const totalPages = Math.ceil(totalItems / pageSize);

      console.log(
          `Starting export of ${totalItems} records over ${totalPages} pages`
      );
      setExportProgress({ loading: true, progress: 0, total: totalItems });

      // Initialize array to store all bills
      let allBills = [];
      const sponsors = await getAllSponsors();

      // Helper function to delay execution
      const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

      // Fetch data in chunks with delay
      for (let page = 1; page <= totalPages; page++) {
        const currentOffset = page - 1; // Convert 1-based page to 0-based offset
        console.log(`Fetching page ${page}/${totalPages}`);

        // Add delay between requests (1 second)
        if (page > 1) {
          await delay(500);
        }

        try {
          console.log(`Requesting page ${page} with offset: ${currentOffset}`);

          const response = await axios.post(
              `${process.env.REACT_APP_LAWS_BACKEND_HOST}/bills/v1/bills/search?includeCounts=false&sort=billType.sortOrder,desc&sort=billNumber,asc&sort=draft.draftNumber,asc&limit=${pageSize}&offset=${currentOffset}`,
              memoizedSearchPayload
          );

          if (
              response.data &&
              response.data.content &&
              Array.isArray(response.data.content)
          ) {
            console.log(
                `Received ${response.data.content.length} bills for page ${page}`
            );

            // Process current chunk
            const processedContent = await processContentWhenReady(
                response.data.content,
                allRequestorData
            );
            console.log(
                `Processed ${processedContent.length} bills for page ${page}`
            );

            // Add sponsor information
            const withSponsors = processedContent.map((bill) => ({
              ...bill,
              sponsor: sponsors.find((s) => s.id === bill.sponsorId) || null,
            }));

            // Add to accumulated bills
            allBills = [...allBills, ...withSponsors];

            console.log(
                `Accumulated ${allBills.length} bills so far (page ${page}/${totalPages})`
            );

            // Update progress
            setExportProgress({
              loading: true,
              progress: allBills.length,
              total: totalItems,
            });
          } else {
            console.warn(
                `Invalid response format for page ${page}:`,
                response.data
            );
            // Add a longer delay if we didn't get any content
            await delay(1000);
          }
        } catch (error) {
          console.error(`Error fetching page ${page}:`, error);
          if (error.response) {
            console.error("Error response data:", error.response.data);
          }
          // Add a longer delay on error
          await delay(2000);
        }
      }

      console.log(
          `All pages processed. Total bills accumulated: ${allBills.length}`
      );

      // Format all bills for export at once
      const exportData = formatLawsBillStructureForDataExport(
          allBills,
          formatToExport
      );
      console.log(`Formatted ${exportData.length} records for export`);

      // Set the export data
      setDataForExport(exportData);
      setExportDataReady(true);

      // Keep progress visible briefly before clearing
      setTimeout(() => {
        setExportProgress({ loading: false, progress: 0, total: 0 });
      }, 2000);
    } catch (error) {
      console.error("Export error:", error);
      setExportProgress({ loading: false, progress: 0, total: 0 });
      setDataForExport(null);
      setExportDataReady(false);
      alert("An error occurred while preparing the export. Please try again.");
    }
  };

  const ExportNotification = () => {
    const { loading, progress, total } = exportProgress;

    if (!loading) return null;

    const percentage = total === 0 ? 0 : Math.round((progress / total) * 100);

    return (
        <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1050 }}>
          <div
              className="toast show"
              role="alert"
              aria-live="polite"
              aria-atomic="true"
          >
            <div className="toast-header">
              <strong className="me-auto">Preparing Export</strong>
            </div>
            <div className="toast-body">
              <div className="d-flex flex-column">
                <div className="mb-2">
                  {total === 0 ? (
                      "Calculating total records..."
                  ) : (
                      <>
                        Processing records: {progress.toLocaleString()} of{" "}
                        {total.toLocaleString()}
                        <br />
                        {percentage}% complete
                      </>
                  )}
                </div>
                <div className="progress" style={{ height: "20px" }}>
                  <div
                      className="progress-bar progress-bar-striped progress-bar-animated"
                      role="progressbar"
                      style={{ width: `${percentage}%` }}
                      aria-valuenow={percentage}
                      aria-valuemin="0"
                      aria-valuemax="100"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
    );
  };

  const handleColumnSelect = (value) => {
    let updatedSelection;
    if (selectedHeaderValue.includes(value)) {
      updatedSelection = selectedHeaderValue.filter((item) => item !== value);
    } else {
      updatedSelection = [...selectedHeaderValue, value];
    }

    // Use setSelectedHeaders instead of directly setting state
    setSelectedHeaders(updatedSelection);

    // Set the cookie with the updated selection
    setCookie("selectedColumns", JSON.stringify(updatedSelection), {
      path: "/",
      maxAge: 604800,
    });
  };

  const setSelectedHeaders = (selectedValueArray) => {
    // First update the selectedHeaderValue
    setSelectedHeaderValue(selectedValueArray);

    // Then create a new tableHeading array with updated displayHeader values
    const updatedTableHeading = tableHeading.map((tableHeader) => ({
      ...tableHeader,
      displayHeader:
          !tableHeader.dynamicHeader ||
          selectedValueArray.includes(tableHeader.field),
    }));

    // Update the tableHeading state
    setTableHeading(updatedTableHeading);
  };

  const memoizedSearchPayload = useMemo(
      () => buildSearchPayload(),
      [
        billSearchText,
        selectedBillTypes,
        selectedLegislator,
        selectedCommittee,
        selectedChambers,
        hasAmendment,
        hasFiscalNotes,
        hasLegalNotes,
        selectedProgress,
        selectedDeadlines,
        selectedDrafter,
        hasPreIntroductionRequired,
        requestor,
        requestorType,
        byRequestOf,
        byRequestOfType,
        selectedActionTypes,
        selectedChapters,
        statusStartDate,
        statusEndDate,
        subjectCode,
        selectedParties,
      ]
  );

  return (
      <div>
        <Header
            setSideNavToggle={setSideNavToggle}
            sideNavToggle={sideNavToggle}
            dataForExport={dataForExport}
            setBillSearchText={setBillSearchText}
            loadExportData={loadExportData}
            exportDataReady={exportDataReady}
            setExportDataReady={setExportDataReady}
            setOffset={setOffset}
            sessionInfoForHeader={sessionInfoForHeader}
        />
        {/* Add the notification component */}
        <ExportNotification />
        <MDBRow>
          <MDBCol>
            {sessionId < 1000 ? (
                <MDBDropdown
                    title="Column Toggle"
                    className="mb-2"
                >
                  <MDBDropdownToggle>
                    <MDBIcon fas icon="columns" />
                  </MDBDropdownToggle>
                  <MDBDropdownMenu>
                    {selectHeaders.map((header) => (
                        <MDBDropdownItem
                            key={header.value}
                            link
                            onClick={(e) => {
                              if (
                                  e.target.tagName !== "INPUT" &&
                                  e.target.tagName !== "LABEL"
                              ) {
                                handleColumnSelect(header.value);
                              }
                            }}
                            active={selectedHeaderValue.includes(header.value)}
                            style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex align-items-center w-100">
                            <MDBCheckbox
                                id={header.value}
                                label={header.text}
                                value={header.value}
                                checked={selectedHeaderValue.includes(header.value)}
                                onChange={() => handleColumnSelect(header.value)}
                            />
                          </div>
                        </MDBDropdownItem>
                    ))}
                  </MDBDropdownMenu>
                </MDBDropdown>
            ) : (
                <></>
            )}
          </MDBCol>
          <MDBCol>
            <Pagination
                offsetPage={offset}
                setOffsetPage={setOffset}
                totalPages={totalPages}
                lastPage={lastPage}
            />
          </MDBCol>
        </MDBRow>
        <MDBDatatable
            data={bills}
            className="bills-datatable"
            hover
            style={{ overflow: "auto" }}
            entries={1000}
            fixedHeader
            pagination={false}
            isLoading={loading}
            striped
            bordered
            noFoundMessage={noResultsMessage}
            //Nothing matches the search criteria.
            loadingMessage="Loading Bills..."
            sortField="Bill #"
            sortdesc="asc"
            maxHeight="100vh"
        />
        <MDBFooter className="d-flex justify-content-between align-items-center mt-2 pb-2">
          {totalElements ? (
              <small className="small">{totalElements} Total Bills</small>
          ) : (
              <></>
          )}
          <Pagination
              offsetPage={offset}
              setOffsetPage={setOffset}
              totalPages={totalPages}
              lastPage={lastPage}
          />
        </MDBFooter>
      </div>
  );
};

export default BillsTableCurrent;
